import {measurementId} from 'utils/funnel';
import {fileType, type ScanMeasurement} from 'utils/funnel';
import {gtmAppScanError, gtmScanMeasurement} from 'utils/gtm';
import {formatAiScore} from 'lib/formatAiScore';
import {makeAutoObservable, runInAction, when} from "mobx";
import {translate} from "i18n/translator";
import {LocalizedText, resolveText} from "i18n/text";
import {uploadFile, getUploadStatus, pollUploadReady, scanRequest, waitTaskResult, getReport, downloadCertificate, AI_TASK_RESULT_MAX_TRIES, IUploadFileResponse} from "requests";
import {FileRequestResult, IUserSettings} from "types";
import {RootStore} from "stores/rootStore";
import {failureReason} from "stores/toastStore";
import type {ActiveSection, ScanModeId, ScanModes} from "stores/inputStore";
import type {ScanModeFlags} from "scanParams";
import {getNowScannedString, limitErrorToTrigger, reprNetworkError, checkError, scoreToPercent, sleep, ErrorType} from "utils";
import {actionableGrammarMatches} from "stores/grammarScanStore";
import {CertificateSource, hasCertificate} from "utils/certificate";
import type {PlanRequirement} from "utils/planCoverage";
import {gtmAppScanStarted, gtmAppScanSuccess, gtmCertificate, type ScanEntrypoint} from "utils/gtm";
import {logEvent} from "analytics";
import {countWords} from "utils/text";
import {AiTone, grammarTone, ScoreKind, shownTone} from "utils/aiScore";
import type {EditorCut, ScanCut} from "stores/editorStore";


interface IFileUUIDValue extends IUploadFileResponse {
    file: File;
    error?: LocalizedText;
    uploadProgress?: number;
    /** True once the upload POST has settled — either succeeded (server
     *  returned the BatchFile) or failed (network/server error). Drives
     *  isFilesLoading; it must NOT depend on `id`, because a rejected file
     *  may settle without one. Landing-injected files are settled from the start. */
    uploaded?: boolean;
    /** The upload request failed (offline, dropped connection, backend rejection): `uploaded`
     *  is set, yet the backend holds no document for it, so its row keeps the progress it
     *  reached instead of showing it complete. */
    uploadFailed?: boolean;
    /** True while the file waits in the client-side upload queue for a free OCR slot
     *  (see MAX_CONCURRENT_OCR). Such files haven't been sent to the backend yet — the
     *  row shows a "pending" label instead of a progress bar. */
    queued?: boolean;
    /** Set when the user took the paywall's "scan the first N chars/words" offer for this
     *  document: it is still over the per-file char limit, but it IS being scanned, so the row
     *  must stop showing the "it won't be scanned" warning (see fileDisplayError). Only
     *  sendRequest sets it, and only on the file it actually sends. */
    charLimitWaived?: boolean;
}

/** The field a batched file carries each tab's state in. */
export const SECTION_FILE_STATE = {AI: 'scan', Plagiarism: 'plagiarism', Grammar: 'grammar'} as const;

export const NO_MODES: ScanModeFlags = {ai: false, deep: false, plagiarism: false, grammar: false};

/** The tools a batch opened from a tab starts with: that tab's own scan, the AI tab in its depth. */
export const modesForSection = (section: ActiveSection, deep: boolean): ScanModeFlags => ({
    ...NO_MODES,
    ...(section === 'AI' ? {ai: !deep, deep} : {[SECTION_FILE_STATE[section]]: true}),
});

/** Whether a selection covers the scan a tab shows. */
export const modesCoverSection = (modes: ScanModeFlags, section: ActiveSection): boolean =>
    section === 'AI' ? modes.ai || modes.deep : modes[SECTION_FILE_STATE[section]];

type CheckState = "ready" | "waiting" | "not_requested" | "error";

type RanCheck = 'scan' | 'plagiarism' | 'grammar';
/** A file's check as its report item names it. */
const RAN_CHECK_TYPE: Record<RanCheck, string> = {scan: 'ai', plagiarism: 'plagiarism', grammar: 'grammar'};

/** A file of a batch that ran, as a login carries it across an OAuth reload. */
export interface RanFile {
    uuid: string;
    name: string;
    analysis: string;
    scannedOn?: string;
    oneTime?: boolean;
}

export interface RanBatchDraft {
    files: RanFile[];
    modes: ScanModeFlags;
    selected: string | null;
}

/** One document scanned on a prefix of its text: `text` is what is sent, `cut` where it stops. */
export type FilePrefix = {uuid: string, text: string, cut: ScanCut};

interface IWaitingFile {
    plagiarism: CheckState;
    scan: CheckState;
    grammar: CheckState;
    name: string;
    scannedOn?: string;
    /** Set when the backend rejected this file for a plan-limit reason. Shown as a
     *  warning triangle + tooltip on the plate (not the red "Error" state). */
    limitMessage?: LocalizedText;
    /** This file's own scan/plagiarism failure (non-limit). Surfaced as the inline error
     *  banner only while this file is selected — the selectedFile reaction in
     *  stores/actions.tsx mirrors it into input.error. Kept per-file so a failed file's
     *  error doesn't bleed onto a different, successfully-scanned file. */
    error?: ErrorType;
    /** Paid for by a one-time scan: it takes no slot of the plan's per-batch document cap. */
    oneTime?: boolean;
    /** A "Scan first N" run's cut over the whole document, and the document as staged — what
     *  goes back in the queue when the user asks for the rest (restagePartial). */
    partial?: {cut: EditorCut, file: IFileUUIDValue};
}

export type ResultChipTool = 'ai' | 'plagiarism' | 'grammar';

/** What a finished check shows on its chip: the reading and the legend circle it earns. */
type ChipVerdict = {value: string; tone: AiTone};

/** A verdict chip on the file plate — one per tool the batch ran on the file. */
export interface ResultChip {
    tool: ResultChipTool;
    status: 'waiting' | 'error' | AiTone;
    /** The AI / plagiarism percent, the grammar issue count — once the check is in. */
    value?: string;
}

export class BatchFileStore {
    fileUuidMap: Map<string, IFileUUIDValue> = new Map<string, IFileUUIDValue>();
    filesWaiting: Map<string, IWaitingFile> = new Map<string, IWaitingFile>();
    filesResults: Map<string, FileRequestResult> = new Map<string, FileRequestResult>();
    responseCounter: number = 0;
    isDownloadingCertificates = false;
    selectedFile: string | null = null;
    /** The tools this batch runs — the bottom panel's chips. Preset from the tab the first
     *  file was dropped on (enterFromTab); every later file, and the files added after a run,
     *  keep it. `ai` and `deep` are the AI pair, at most one of them on. */
    modes: ScanModeFlags = NO_MODES;
    root: RootStore
    controller = new AbortController();
    private progressIntervals: Map<string, NodeJS.Timeout> = new Map();

    /** Client-side upload queue. The backend caps concurrent OCR jobs per user
     *  (PDF_OCR_MAX_CONCURRENT_PER_USER, default 5) and rejects the rest with 406
     *  too_many_ocr_in_progress. Instead of firing every upload at once and bouncing
     *  off that limit, we admit at most MAX_CONCURRENT_OCR files at a time and hold each
     *  slot for the file's whole lifecycle (upload + async OCR), so a slot frees only
     *  when a document is actually done. Queued files show as "pending" until admitted. */
    private uploadQueue: Array<() => Promise<void>> = [];
    private activeUploads = 0;
    private readonly MAX_CONCURRENT_OCR = 5;

    /** Files chosen before the session is ready (no token/user yet). Held here and
     *  replayed through onFileDrop once user.isReady(), so an early selection is never
     *  lost and we never upload with `Token null`. See onFileDrop. */
    private _pendingDrop: File[] | null = null;

    constructor(root: RootStore) {
        this.root = root
        makeAutoObservable(this);
    }

    /** Some file still has a check in flight. */
    get isRunning() {
        return Array.from(this.filesWaiting.values()).some(f => f.scan === 'waiting' || f.plagiarism === 'waiting' || f.grammar === 'waiting');
    }

    get isBatchMode() {
        return this.filesWaiting.size > 0
    }

    get isFilesMode() {
        return this.fileUuidMap.size > 0
    }

    /** The documents on the Batch Scan tab the backend holds, for the app to take over: the
     *  staged ones whose upload landed, then the ones a run took. */
    get handoffUuids(): string[] {
        const staged = Array.from(this.fileUuidMap.entries())
            .filter(([, f]) => f.uploaded && !f.uploadFailed && !f.error).map(([uuid]) => uuid);
        return Array.from(new Set([...staged, ...this.filesWaiting.keys()]));
    }

    /** The Batch Scan tab is up: files staged, or a batch that ran. */
    get isActive() {
        return this.isFilesMode || this.isBatchMode
    }

    /** Files that ran on one-time scans, outside the plan's document cap. */
    get oneTimeDocumentCount() {
        return Array.from(this.filesWaiting.values()).filter(file => file.oneTime).length;
    }

    /** Files this batch already ran on the plan, each holding a slot of its document cap. */
    get planRanCount() {
        return this.filesWaiting.size - this.oneTimeDocumentCount;
    }

    /** The "N / limit documents" counter: the staged files and those the batch ran on the plan. */
    get planDocumentCount() {
        return this.fileUuidMap.size + this.planRanCount;
    }

    /** How many more files the plan's per-batch document cap lets this batch run: the files it
     *  already ran keep their slots, so "Upload more" after a run can't reset the count. */
    get openDocumentSlots() {
        return Math.max(0, (this.root.user.user?.batch_doc_upload ?? 0) - this.planRanCount);
    }

    get hasAnyMode() {
        return Object.values(this.modes).some(Boolean)
    }

    setModes(modes: ScanModeFlags) {
        this.modes = modes;
    }

    /** The AI pair is a segmented control with a way off: one depth on, or neither. */
    setAiDepth(depth: 'ai' | 'deep' | null) {
        this.modes = {...this.modes, ai: depth === 'ai', deep: depth === 'deep'};
    }

    /** One tool as the whole selection — the phone's menu, which shows one result at a time. */
    selectOnlyMode(mode: ScanModeId) {
        this.modes = {...NO_MODES, [mode]: true};
    }

    /** A chip press: the add-ons toggle, an AI depth turns on — or off, pressed while on. */
    toggleMode(mode: ScanModeId) {
        if (mode === 'ai' || mode === 'deep') this.setAiDepth(this.modes[mode] ? null : mode);
        else this.modes = {...this.modes, [mode]: !this.modes[mode]};
    }

    /** The chip for this tab's scan is on — the grey dot on the tab until the batch runs. */
    sectionSelected(section: ActiveSection) {
        return modesCoverSection(this.modes, section);
    }

    /** The first drop opens the batch with the tab's own scan selected; later drops keep the
     *  selection, as does a batch that already ran (its tools are what the new files get). */
    private enterFromTab() {
        if (this.isActive) return;
        // The text's whole run, its extra tools with it.
        this.modes = this.root.input.textModes;
    }

    /** The batch's way out, whatever the surface: everything the page holds goes. */
    exit() {
        this.root.input.clear();
    }

    /** The close on the tab and the clear-all: a batch that ran leaves at once (its results
     *  are in the history); one that has not asks first, since its files go with it. */
    requestExit() {
        if (this.isBatchMode) this.exit();
        else this.root.modal.showModal('batch-leave');
    }

    /** Whether this batch asked for the scan a tab shows at all. A tab it never ran shows
     *  no batch — no file list, an empty editor — rather than files with nothing in them.
     *  A batch that ran nothing anywhere (every file refused on a limit) shows on every tab:
     *  the file list is where its limit messages and the only exit live. */
    sectionRan(section: ActiveSection) {
        const ranAt = (field: keyof IWaitingFile) => {
            for (const file of this.filesWaiting.values()) {
                if (file[field] !== 'not_requested') return true;
            }
            return false;
        };
        const ranAnywhere = (Object.values(SECTION_FILE_STATE) as (keyof IWaitingFile)[]).some(ranAt);
        return !ranAnywhere || ranAt(SECTION_FILE_STATE[section]);
    }

    /** `sectionRan` for one mode: the AI tab holds two depths, and the batch ran only the one it picked. */
    modeRan(mode: ScanModeId) {
        const section: ActiveSection = mode === 'plagiarism' ? 'Plagiarism' : mode === 'grammar' ? 'Grammar' : 'AI';
        return this.sectionRan(section) && (section !== 'AI' || this.modes[mode]);
    }

    deleteFile(uuid: string) {
        this.root.input.setError("")
        // Clear progress interval if exists
        const interval = this.progressIntervals.get(uuid);
        if (interval) {
            clearInterval(interval);
            this.progressIntervals.delete(uuid);
        }
        this.fileUuidMap.delete(uuid);
        // The last file taken off a batch that never ran closes it — there is nothing left
        // to ask about.
        if (!this.isActive) this.exit();
    }

    /** Опрашивает бекенд, пока асинхронный OCR не завершится (status != processing).
     *  Параллельно ведёт прогресс-бар реальным прогрессом OCR (90→99%, холд под 100). */
    async pollUploadReady(uuid: string, initial: IUploadFileResponse): Promise<IUploadFileResponse> {
        // Останавливаем «холд»-анимацию заливки — дальше прогресс ведёт реальный OCR.
        const interval = this.progressIntervals.get(uuid);
        if (interval) {
            clearInterval(interval);
            this.progressIntervals.delete(uuid);
        }
        return pollUploadReady(uuid, initial, {
            isAlive: () => this.fileUuidMap.has(uuid),
            // ещё processing — отражаем реальный OCR-прогресс по страницам. Бекендовый
            // progress может скакать (per-page, рестарты), поэтому держим бар монотонным
            // (Math.max), иначе в UI он откатывается назад (90→93→90).
            onProgress: status => {
                const data = this.fileUuidMap.get(uuid);
                if (!data) return;
                const next = 90 + (status.progress ?? 0) * 0.09;
                this.fileUuidMap.set(uuid, {...data, uploadProgress: Math.max(data.uploadProgress ?? 90, next)});
            },
        });
    }

    getFileError(file: IFileUUIDValue): LocalizedText | undefined {
        if (file.status === 'processing') {
            return undefined;  // документ ещё извлекается асинхронным OCR — это не ошибка
        }
        if (file.status === 'failed') {
            // OCR не смог прочитать документ (битый шрифт без текста / пустой скан)
            return () => translate('scan-error-file-too-short');
        }
        if (file.file.size === 0) {
            return () => translate('scan-error-file-empty');
        }
        if (!file.is_english) {
            return () => translate('scan-error-file-language');
        }
        // NB: the per-file character-limit check is intentionally NOT cached here — it depends
        // on the user's current plan limit and is evaluated live in isOverCharLimit(), so
        // raising the limit (e.g. after an upgrade) immediately unblocks a file that now fits.
        if (file.characters_count && file.characters_count <= 200) {
            return () => translate('scan-error-file-too-short');
        }
    }

    /** Same validation as getFileError but for files handed off from the
     *  landing, where we only have server metadata and never the original
     *  bytes — emptiness is derived from characters_count, not File.size. */
    private getSharedFileError(file: IFileUUIDValue): LocalizedText | undefined {
        if (file.status === 'processing') {
            return undefined;  // landing-файл ещё извлекается асинхронным OCR — это не ошибка
        }
        if (file.status === 'failed') {
            // OCR не смог прочитать документ (битый шрифт без текста / пустой скан)
            return () => translate('scan-error-file-too-short');
        }
        if (!file.characters_count) {
            return () => translate('scan-error-file-empty');
        }
        if (!file.is_english) {
            return () => translate('scan-error-file-language');
        }
        // Per-file char-limit is evaluated live in isOverCharLimit(), not cached here.
        if (file.characters_count <= 200) {
            return () => translate('scan-error-file-too-short');
        }
    }

    /** Record a file's intrinsic validation result on its row and raise the inline error
     *  banner. Only hard, plan-independent errors (empty / wrong language / too short / OCR
     *  failed) are cached here; the per-file char-limit is a plan limit evaluated live
     *  (isOverCharLimit) and surfaced as a paywall CTA on "Run scan", never as a banner. */
    private applyFileError(data: IFileUUIDValue, fileError: LocalizedText | undefined, filename: string) {
        data.error = fileError;
        if (fileError) {
            this.root.input.setError(() => translate('scan-error-file-with-name', {filename, message: resolveText(fileError)}));
        }
    }

    /** Populate the upload widget with files already uploaded on the landing
     *  (handed off via a one-time token, see init.js getParamsFiles). They show
     *  up exactly as a finished manual upload — full metadata, ready to scan —
     *  and the hand-off then presses Run scan itself (init.js). We hold a
     *  placeholder File since the bytes live server-side; the scan runs by uuid. */
    injectUploadedFiles(files: IUploadFileResponse[]) {
        this.enterFromTab();
        for (const file of files) {
            const isProcessing = file.status === 'processing';
            const data: IFileUUIDValue = {
                ...file,
                file: new File([], file.file_name || "document"),
                // processing-файл (handoff с лендинга до завершения OCR) ещё не готов к
                // скану — держим uploaded=false, чтобы Run-scan gate его не пропустил.
                uploadProgress: isProcessing ? 90 : 100,
                uploaded: !isProcessing,
            };
            if (isProcessing) {
                this.fileUuidMap.set(file.uuid, data);
                // OCR ещё идёт на бекенде — дожидаемся готовности, как делает ручной addFile.
                // Без этого файл с пустым characters_count ложно считался пустым и выпадал из скана.
                void this.pollUploadReady(file.uuid, file).then(ready => {
                    if (!this.fileUuidMap.has(file.uuid)) return;
                    const updated = {...this.fileUuidMap.get(file.uuid)!, ...ready, uploadProgress: 100, uploaded: true};
                    this.applyFileError(updated, this.getSharedFileError(updated), file.file_name || "document");
                    this.fileUuidMap.set(file.uuid, updated);
                }, e => {
                    if (this.fileUuidMap.has(file.uuid)) this.failUpload(file.uuid, e);
                });
            } else {
                // Validated BEFORE the row is stored: the map is observable and keeps its own copy
                // of the object, so a verdict written to `data` afterwards never reaches the row —
                // and an unscannable file would look ready to everything that reads it (the Run
                // scan gate, the hand-off's autorun, the paywall).
                this.applyFileError(data, this.getSharedFileError(data), file.file_name);
                this.fileUuidMap.set(file.uuid, data);
                // The landing handoff (getSharedFiles) deliberately omits the extracted text, but
                // the single-document paywall offer cuts its prefix client-side — without the text
                // a document arriving from the landing gets no free option where the same document
                // uploaded here does. Fetch it once, and only for a lone document: that is the only
                // shape the offer applies to, so a multi-file handoff pays nothing for this.
                if (!data.text && files.length === 1) void this.fetchExtractedText(file.uuid);
            }
        }
    }

    /** Fill in a file's extracted text after the fact (see injectUploadedFiles). Best effort:
     *  on failure the document simply keeps no prefix offer, exactly as before. */
    private async fetchExtractedText(uuid: string) {
        try {
            const ready = await getUploadStatus(uuid);
            const data = this.fileUuidMap.get(uuid);
            if (data && ready.text) this.fileUuidMap.set(uuid, {...data, text: ready.text});
        } catch (e) {
            // Nothing to recover: the offer stays unavailable, the paywall still upsells.
        }
    }

    /** Server-settled uploads to carry across a login redirect (login wipes the
     *  in-memory store). We keep only the IUploadFileResponse metadata — the File
     *  bytes can't be serialized; restore re-injects via injectUploadedFiles, which
     *  re-creates a placeholder File and re-derives per-file validation. In-flight or
     *  network-failed uploads (no server id) are skipped — they can't be restored. */
    getPersistableUploadedFiles(): IUploadFileResponse[] {
        return Array.from(this.fileUuidMap.values())
            .filter(f => f.uploaded && !!f.uuid && !!f.id)
            .map(({file, error, uploadProgress, uploaded, ...rest}) => rest);
    }

    private startProgressAnimation(uuid: string, startProgress: number, endProgress: number, duration: number) {
        // Clear any existing interval for this UUID
        const existingInterval = this.progressIntervals.get(uuid);
        if (existingInterval) {
            clearInterval(existingInterval);
        }

        const startTime = Date.now();
        const progressDiff = endProgress - startProgress;
        
        const interval = setInterval(() => {
            const elapsed = Date.now() - startTime;
            const data = this.fileUuidMap.get(uuid);

            if (elapsed >= duration) {
                // Animation complete
                clearInterval(interval);
                this.progressIntervals.delete(uuid);
                if (data) {
                    this.fileUuidMap.set(uuid, {...data, uploadProgress: endProgress});
                }
            } else {
                // Calculate current progress
                const progress = startProgress + (progressDiff * (elapsed / duration));
                if (data) {
                    this.fileUuidMap.set(uuid, {...data, uploadProgress: Math.min(progress, endProgress)});
                }
            }
        }, 20);

        this.progressIntervals.set(uuid, interval);
    }

    /** Бекенд держит per-user лимит одновременных OCR (PDF_OCR_MAX_CONCURRENT_PER_USER):
     *  лишние тяжёлые файлы из большого батча отлетают с 406 too_many_ocr_in_progress.
     *  Слот освобождается, когда завершается предыдущий OCR, — ждём с backoff и повторяем
     *  загрузку, вместо того чтобы навсегда пометить файл ошибкой. Зеркало scanFileWithRetry. */
    private async uploadFileWithRetry(
        file: File, uuid: string, onProgress: (progress: number) => void,
        attempts = 40, delayMs = 3000,
    ): Promise<IUploadFileResponse> {
        for (let i = 0; ; i++) {
            try {
                return await uploadFile(file, uuid, onProgress) as IUploadFileResponse;
            } catch (e) {
                const isOcrBusy = checkError(reprNetworkError(e).code, "too_many_ocr_in_progress");
                if (!isOcrBusy || i >= attempts - 1 || this.controller.signal.aborted || !this.fileUuidMap.has(uuid)) throw e;
                await sleep(delayMs);
            }
        }
    }

    addFile(file: File) {
        const uuid = URL.createObjectURL(new Blob()).split('/').pop() as string;
        this.fileUuidMap.set(uuid, {
            id: "",
            file_name: file.name,
            size: 0,
            type: "",
            url: "",
            characters_count: undefined,
            words_count: undefined,
            file_size: "-",
            uuid: uuid,
            file: file,
            is_english: true,
            uploadProgress: 0,
            uploaded: false,
            queued: true,
        });
        this.uploadQueue.push(() => this.runUpload(file, uuid));
        this.pumpUploadQueue();
    }

    /** Admit queued uploads up to MAX_CONCURRENT_OCR, refilling a slot the moment a
     *  file's whole lifecycle (upload + OCR) settles. Re-entrant: each finished task
     *  pumps the next, so the queue drains as fast as the backend frees OCR capacity. */
    private pumpUploadQueue() {
        while (this.activeUploads < this.MAX_CONCURRENT_OCR && this.uploadQueue.length > 0) {
            const task = this.uploadQueue.shift()!;
            this.activeUploads++;
            void task().finally(() => {
                this.activeUploads--;
                this.pumpUploadQueue();
            });
        }
    }

    private async runUpload(file: File, uuid: string): Promise<void> {
        // Dropped while still waiting in the queue — release the slot, do nothing.
        if (!this.fileUuidMap.has(uuid)) return;
        const uploadStartedAt = performance.now();
        const uploadContext = {document_id: uuid, file_type: fileType(file.name), file_bytes: file.size};
        logEvent(gtmScanMeasurement('GTM_document_upload_started', uploadContext));
        // Admitted: leave the pending state and start the real upload.
        this.fileUuidMap.set(uuid, {...this.fileUuidMap.get(uuid)!, queued: false});
        try {
            const e = await this.uploadFileWithRetry(file, uuid, (progress) => {
                const data = this.fileUuidMap.get(uuid);
                if (progress >= 99.9) {
                    // Bytes are sent, but the backend still has to parse the file and
                    // return words_count. Crawl to 99% and HOLD there — never 100% — so
                    // the bar can't visually "complete" while the file isn't actually
                    // scannable yet. The jump to 100% (green) happens the moment
                    // `uploaded` flips true, keeping the bar in lockstep with the Run-scan
                    // gate (isFilesLoading → !uploaded). Without the cap, a backend slower
                    // than this animation left the bar at 100% with the button disabled.
                    this.startProgressAnimation(uuid, 90, 99, 5000);
                }
                if (data) {
                    this.fileUuidMap.set(uuid, {...data, uploadProgress: progress * 0.9});
                }
            });
            // Skip if fileUuidMap was already cleared (scan started)
            if (!this.fileUuidMap.has(uuid)) return;
            let fileResponse = {...(e as IUploadFileResponse), uuid};

            // Тяжёлые файлы (битый/сканированный PDF) бекенд извлекает асинхронным
            // OCR и сразу отвечает status=processing — дожидаемся готовности документа.
            if (fileResponse.status === 'processing') {
                fileResponse = {...(await this.pollUploadReady(uuid, fileResponse)), uuid};
                if (!this.fileUuidMap.has(uuid)) return;
            }
            let data = this.fileUuidMap.get(uuid)!;

            // Set progress to 100% when upload completes. The upload has
            // settled even if client-side validation below flags the file.
            data = {...data, ...fileResponse, file: file, uploadProgress: 100, uploaded: true};
            this.applyFileError(data, this.getFileError(data), file.name);
            this.fileUuidMap.set(uuid, data);
            logEvent(gtmScanMeasurement('GTM_document_upload_completed', {
                ...uploadContext, document_status: fileResponse.status ?? 'ready',
                text_chars: fileResponse.characters_count, word_count: fileResponse.words_count,
                client_validation_passed: !data.error,
                elapsed_ms: Math.round(performance.now() - uploadStartedAt),
            }));
        } catch (e) {
            // Skip if fileUuidMap was already cleared (scan started)
            if (!this.fileUuidMap.has(uuid)) return;
            logEvent(gtmScanMeasurement('GTM_document_upload_failed', {
                ...uploadContext, error_code: reprNetworkError(e).code || 'unknown',
                elapsed_ms: Math.round(performance.now() - uploadStartedAt),
            }));
            this.failUpload(uuid, e);
        } finally {
            const interval = this.progressIntervals.get(uuid);
            if (interval) {
                clearInterval(interval);
                this.progressIntervals.delete(uuid);
            }
        }
    }

    /** The file never became scannable: its row and the banner say why. */
    private failUpload(uuid: string, e: unknown) {
        const data = this.fileUuidMap.get(uuid)!;
        const error = reprNetworkError(e);
        // Only the backend's own refusal names the problem. Anything else is the connection or
        // our side (an OCR the wait gave up on included), and "network" is wrong for half of it.
        const details: LocalizedText = error.code === 'network'
            ? failureReason(e)
            : () => this.root.input.castErrorText(error.message, error.code, error.translationArgs);
        // Resolve the backend code into copy FIRST, then name the file around it:
        // "…report.pdf: File is larger than the 5 MB limit" beats either half alone.
        const localizedMessage = () => translate('scan-error-upload-failed', {
            filename: data.file_name, details: resolveText(details),
        });
        this.fileUuidMap.set(uuid, {...data, error: localizedMessage, uploaded: true, uploadFailed: true});
        this.root.input.setError({...error, message: localizedMessage}, "", true);
    }

    /** A landing-handed (or just-uploaded) file can be scanned a beat before its
     *  BatchFile is committed on the backend, which returns file_with_uuid_not_found.
     *  The row materializes within seconds, so retry the scan with backoff before
     *  surfacing the error — this is the landing-handoff counterpart of the
     *  isFilesLoading upload gate (injected files are uploaded:true from the start, so
     *  that gate doesn't cover them).
     *
     *  Waiting is all this can do, so it waits briefly. Ownership is not its problem:
     *  the backend hands an anon's pending uploads to the account at login, so a file
     *  still missing after a few seconds is missing for good and the extra naps only
     *  delay the message that says so. */
    private async scanFileWithRetry(
        uuid: string, aiScan: boolean, deepScan: boolean, plagiarism: boolean, grammar: boolean,
        {prefixText, attempts = 4, delayMs = 1500, oneTimeRun = null}:
            {prefixText?: string, attempts?: number, delayMs?: number, oneTimeRun?: string | null} = {},
    ) {
        // A prefix run sends the text inline (see sendRequest): handing over `uuid` would make
        // the backend read the whole document back and defeat the truncation, so the uuid rides
        // along as doc_id instead — the analysis still belongs to the file.
        const [text, fileUuid, docId] = prefixText !== undefined
            ? [prefixText, null, uuid]
            : [null, uuid, null];
        for (let i = 0; ; i++) {
            try {
                return await scanRequest(text, fileUuid, aiScan, deepScan, plagiarism, grammar,
                                         "app", this.controller.signal, docId, oneTimeRun);
            } catch (e) {
                const isNotReady = checkError(reprNetworkError(e).code, "file_with_uuid_not_found");
                if (!isNotReady || i >= attempts - 1 || this.controller.signal.aborted) throw e;
                await sleep(delayMs);
            }
        }
    }

    /**
     * `prefix` scans ONE named file on a prefix of its text instead of the whole document — the
     * free "scan first N chars / N words" offer the paywall makes when a single document is
     * itself too big (there is no "scan fewer files" to offer a batch of one). It is keyed by
     * uuid, so only that file skips the char-limit gate and only it is truncated.
     *
     * `entrypoint` is what started the run, reported by the scan_started events below; it comes
     * from makeRunScanRequest, and only the landing hand-off passes anything but 'run_scan'.
     */
    async sendRequest(plagiarism: boolean = false, deepScan: boolean = false, aiScan: boolean = false, user: IUserSettings, maxFiles?: number, grammar: boolean = false, prefix?: FilePrefix, entrypoint: ScanEntrypoint = 'run_scan', oneTimeRun: string | null = null, oneTimeUuids?: ReadonlySet<string>) {
        this.root.input.setError();
        // A one-time run pays only for `oneTimeUuids` (all files without it); the rest of the
        // batch runs on the plan, under its walls.
        const isOneTime = (uuid: string) => !!oneTimeRun && (!oneTimeUuids || oneTimeUuids.has(uuid));

        // Measured NOW, off the batch as the user staged it. `showLimitModalOnce` below fires
        // from inside the per-file tasks, and by then `fileUuidMap` has been narrowed (next
        // statement) and then emptied ("Clear upload queue after dispatch") — reading it there
        // would size every offer as an empty batch, i.e. no requirement at all.
        // Measured BEFORE the filter, so the files over the per-file char cap count too: lifting
        // that cap is exactly what the offer may be selling.
        const fullBatchRequirement = this.planRequirement(this.root.input.wordCostMultiplier());

        // Filter out files that cannot be scanned — intrinsic errors and files over the live
        // per-file char limit (the latter no longer carry `error`, so check it explicitly).
        // The prefixed file is the exception: the offer exists precisely to scan the first N
        // chars of an oversized document, so that one file has to survive the filter — and it is
        // marked as waived on the way through, or its row would keep warning "it won't be
        // scanned" (a live check, see fileDisplayError) all through the scan it is having.
        this.fileUuidMap = new Map(
            Array.from(this.fileUuidMap.entries())
                .filter(([uuid, file]) => !file.error && (uuid === prefix?.uuid || !this.isOverCharLimit(file, isOneTime(uuid))))
                .map(([uuid, file]): [string, IFileUUIDValue] =>
                    [uuid, uuid === prefix?.uuid || isOneTime(uuid) ? {...file, charLimitWaived: true} : file])
        );

        if (this.fileUuidMap.size === 0) {
            return;
        }

        // Truncation: scan only the first `maxFiles` files and drop the rest entirely —
        // the user explicitly chose to scan the first N, so the others leave the list.
        const allUuids = Array.from(this.fileUuidMap.keys());
        const uuidsToScan = typeof maxFiles === 'number' ? allUuids.slice(0, maxFiles) : allUuids;
        const aiRequested = aiScan || deepScan;

        // A narrowed run is one the user already accepted a concession on ("scan the first N",
        // "scan the first part"), so it is priced off what it actually sends — otherwise a limit
        // error on the concession would be charged at the price of the batch it replaced.
        const stagedRequirement = typeof maxFiles === 'number' || prefix
            ? this.planRequirement(this.root.input.wordCostMultiplier(), {uuids: uuidsToScan, prefix})
            : fullBatchRequirement;

        // The big paywall is shown at most once per batch, even if several files come
        // back with a limit error; each affected plate still gets its own triangle.
        let limitModalShown = false;
        const showLimitModalOnce = (code?: string) => {
            if (limitModalShown) return;
            limitModalShown = true;
            if (this.root.input.showWidgetDailyLimit(code)) return;
            // NOT isAnonymous: that reads false throughout the widget, whose app flow walls an anon too.
            if (this.root.user.user?.is_anon) {
                // An anon's batch limit is a login wall, not a paywall — the upgrade offer below
                // is priced once there is an account to price it for (see showAnonLimitCta).
                this.root.input.showAnonLimitCta(
                    limitErrorToTrigger(code),
                    {limit_value: code || '', entrypoint: 'batch_scan_limit'},
                );
                return;
            }
            // Free and paid alike: a backend limit error opens the upgrade paywall. Sized off the
            // staged batch so the plan on offer can actually run it — otherwise this path sells a
            // tier the very same batch would be rejected on again.
            // No `onUnblocked` and no one-time scan: by now the files have been dispatched and the
            // queue cleared, so there is no batch left to re-run — the user restages and presses Run scan.
            this.root.input.showPaywall({
                trigger: limitErrorToTrigger(code),
                requirement: stagedRequirement,
            }, {oneTime: false});
        };

        const batchMeasurement: ScanMeasurement = {
            scan_attempt_id: this.root.input.scanAttemptId ?? measurementId(),
            input_type: 'batch', file_count: uuidsToScan.length,
        };
        if (deepScan) {
            logEvent(gtmAppScanStarted({...batchMeasurement, scan_type: 'deep_scan', page_type: 'app_scan', entrypoint}));
        } else if (aiScan) {
            logEvent(gtmAppScanStarted({...batchMeasurement, scan_type: 'simple_scan', page_type: 'app_scan', entrypoint}));
        }
        if (plagiarism) {
            logEvent(gtmAppScanStarted({...batchMeasurement, scan_type: 'plagiarism', page_type: 'app_scan', entrypoint}));
        }
        if (grammar) {
            logEvent(gtmAppScanStarted({...batchMeasurement, scan_type: 'grammar', page_type: 'app_scan', entrypoint}));
        }

        const tasks: Promise<void>[] = [];

        for (const uuid of uuidsToScan) {
            const fileData = this.fileUuidMap.get(uuid);
            const previous = this.filesWaiting.get(uuid)?.partial;
            // A whole run of a partly scanned document keeps its divider, as "scanning the rest".
            const partial = uuid === prefix?.uuid && fileData?.text
                ? {cut: {...prefix.cut, text: fileData.text, scanning: false}, file: {...fileData, charLimitWaived: undefined}}
                : previous && {...previous, cut: {...previous.cut, scanning: true}};
            this.filesWaiting.set(uuid, {
                partial,
                plagiarism: plagiarism ? "waiting" : "not_requested",
                scan: aiRequested ? "waiting" : "not_requested",
                grammar: grammar ? "waiting" : "not_requested",
                name: fileData?.file?.name || fileData?.file_name || "",
                oneTime: isOneTime(uuid),
            });
            // The extracted text is what the editor shows while the scans run; each result
            // then lands over it with its own copy (a hand-off file may carry none).
            if (fileData?.text) this.filesResults.set(uuid, {text: fileData.text} as FileRequestResult);
        }
        // A tab the batch did not ask for is closed while it runs, so the results open on
        // one it did — the first, in tab order.
        this.openRanSection();

        for (const uuid of uuidsToScan) {
            if (!aiRequested && !plagiarism && !grammar) continue;

            const task = (async () => {
                const startedAt = performance.now();
                const measurement: ScanMeasurement = {...batchMeasurement, input_type: 'document', document_id: uuid};
                try {

                    const result = await this.scanFileWithRetry(uuid, aiScan, deepScan, plagiarism, grammar,
                        {prefixText: uuid === prefix?.uuid ? prefix.text : undefined, oneTimeRun: isOneTime(uuid) ? oneTimeRun : null});
                    const analysisUuid = result.analysis_uuid;
                    Object.assign(measurement, {analysis_id: result.analysis_id, analysis_uuid: analysisUuid});
                    this.root.certificate?.rememberAnalysis(measurement);
                    logEvent(gtmScanMeasurement('GTM_app_scan_accepted', {...measurement, truncated: uuid === prefix?.uuid}));

                    // POST /scan reserves this file's words, so reflect it in the
                    // sidebar right away — the single-text path already does this
                    // (inputStore), and a batch that stayed stale until reload was
                    // the odd one out. Failures release the reservation, and the
                    // per-task handlers below refresh again, so the number backs
                    // out on its own. Not awaited: the scan shouldn't wait on it.
                    void this.root.user.refresh();

                    const waitTask = (key: string, task_id: number, transform: any, maxTries?: number) => {
                        waitTaskResult(task_id, this.controller.signal, maxTries).then(
                            (response) => {
                                response = transform(response)
                                const prevState = this.filesWaiting.get(uuid);
                                if (!prevState) return;
                                this.filesWaiting.set(uuid, {...prevState, [key]: "ready",
                                    scannedOn: getNowScannedString()});

                                const prevAnswer = (this.filesResults.get(uuid) || {}) as FileRequestResult;
                                this.filesResults.set(uuid, {...prevAnswer, ...response, analysis_uuid: analysisUuid});
                                this.responseCounter += 1;
                                this.root.announcement.scanLanded();
                            }
                        ).catch(e => {
                            console.error(e);
                            const errInfo = reprNetworkError(e);
                            if (!this.controller.signal.aborted) logEvent(gtmAppScanError({
                                ...measurement, task_id, scan_type: key === 'scan' ? (deepScan ? 'deep_scan' : 'simple_scan') : key,
                                error_code: errInfo.code || 'unknown', error_message: '', error_stage: 'result_poll',
                                page_type: 'app_scan', entrypoint, elapsed_ms: Math.round(performance.now() - startedAt),
                            }));
                            const state = this.filesWaiting.get(uuid);
                            if (state) {
                                this.filesWaiting.set(uuid, {...state, [key]: "error", error: errInfo});
                            }
                            this.responseCounter += 1;
                            // No limit branch here, deliberately: this is the result poll, which
                            // cannot carry a limit code (InputStore.isLimitError says why). The
                            // limit plate and the paywall belong to the POST /scan catch below,
                            // which is where this file's words are refused.
                            // Errors are stored on the file above; the inline banner is raised
                            // by the selectedFile reaction so it shows only for this file.
                        }).finally(() => {
                            // Update limits and history (new scan should appear on page 1)
                            this.root.history.page = 1;
                            this.root.refresh();
                        })
                    }

                    if (result.ai_task_id) {
                        waitTask("scan", result.ai_task_id, (r: any) => {
                            const scanType = deepScan ? 'deep_scan' : 'simple_scan' as const;
                            logEvent(gtmAppScanSuccess({...measurement, task_id: result.ai_task_id, elapsed_ms: Math.round(performance.now() - startedAt), scan_type: scanType, scan_result: String(r.data.score), page_type: 'app_scan', entrypoint: 'ai_scan_result'}));
                            return this.checkResult('scan', r.data);
                        }, AI_TASK_RESULT_MAX_TRIES)
                    }
                    if (result.plagiarism_task_id) {
                        waitTask("plagiarism", result.plagiarism_task_id, (r: any) => {
                            logEvent(gtmAppScanSuccess({...measurement, task_id: result.plagiarism_task_id, elapsed_ms: Math.round(performance.now() - startedAt), scan_type: 'plagiarism', scan_result: String(r.data.score), page_type: 'app_scan', entrypoint: 'plagiarism_result'}));
                            return this.checkResult('plagiarism', r.data);
                        })
                    }
                    if ((result as any).grammar_task_id) {
                        waitTask("grammar", (result as any).grammar_task_id, (r: any) => {
                            logEvent(gtmAppScanSuccess({...measurement, task_id: result.grammar_task_id, elapsed_ms: Math.round(performance.now() - startedAt), scan_type: 'grammar', scan_result: String(r.data?.stats?.total ?? ''), page_type: 'app_scan', entrypoint: 'grammar_result'}));
                            return this.checkResult('grammar', r.data);
                        })
                    }

                } catch (e) {
                    // AI error handling
                    const errorInfo = reprNetworkError(e);
                    if (!this.controller.signal.aborted) logEvent(gtmAppScanError({
                        ...measurement, scan_type: 'batch', error_code: errorInfo.code || 'unknown',
                        error_message: '', error_stage: 'scan_request', page_type: 'app_scan', entrypoint,
                        elapsed_ms: Math.round(performance.now() - startedAt),
                    }));
                    const isLimit = this.root.input.isLimitError(errorInfo.code);
                    const state = this.filesWaiting.get(uuid);
                    if (state) {
                        // Only the checks this run asked for failed — one that was never
                        // requested stays that way (its chip must not appear).
                        const failed = (check: CheckState): CheckState => check === "waiting" ? "error" : check;
                        this.filesWaiting.set(uuid, isLimit
                            ? {...state, scan: "not_requested", plagiarism: "not_requested", grammar: "not_requested", limitMessage: () => translate('scan-error-file-limit-reached')}
                            : {...state, scan: failed(state.scan), plagiarism: failed(state.plagiarism), grammar: failed(state.grammar), error: errorInfo});
                    }
                    this.responseCounter += 1;
                    if (isLimit) {
                        showLimitModalOnce(errorInfo.code);
                    }
                    // Non-limit errors are stored on the file above; the inline banner is
                    // raised by the selectedFile reaction so it shows only for this file.
                }
            })();

            tasks.push(task);
        }

        // Clear upload queue after dispatch
        this.fileUuidMap = new Map<string, IFileUUIDValue>();
        await Promise.allSettled(tasks);
    }

    /** A ran file's AI check again, deep — the way from a simple result to its locked
     *  composition. The file's other checks keep their results. */
    async rescanDeep(uuid: string) {
        const before = this.filesWaiting.get(uuid);
        const text = this.filesResults.get(uuid)?.text;
        if (!before || before.scan === 'waiting' || !text) return;
        const startedAt = performance.now();
        const measurement: ScanMeasurement = {scan_attempt_id: measurementId(), input_type: 'document', document_id: uuid};
        const update = (state: Partial<IWaitingFile>, result?: Partial<FileRequestResult>) => runInAction(() => {
            // Cleared or removed while the scan ran: writing it back would resurrect the file.
            if (!this.filesWaiting.has(uuid)) return;
            this.filesWaiting.set(uuid, {...this.filesWaiting.get(uuid)!, ...state});
            if (result) this.filesResults.set(uuid, {...this.filesResults.get(uuid), ...result} as FileRequestResult);
            this.responseCounter += 1;
        });
        update({scan: 'waiting', error: undefined});
        logEvent(gtmAppScanStarted({...measurement, scan_type: 'deep_scan', page_type: 'app_scan', entrypoint: 'run_scan'}));
        try {
            // Under the file's doc_id: the backend runs its analysis's AI check again, deep, and the
            // file keeps one analysis with its other checks. The text inline, as it was scanned.
            const result = await scanRequest(text, null, true, true, false, false, "app", this.controller.signal, uuid);
            void this.root.user.refresh();
            const response: any = await waitTaskResult(result.ai_task_id, this.controller.signal, AI_TASK_RESULT_MAX_TRIES);
            Object.assign(measurement, {analysis_id: result.analysis_id, analysis_uuid: result.analysis_uuid});
            this.root.certificate?.rememberAnalysis(measurement);
            logEvent(gtmAppScanSuccess({...measurement, task_id: result.ai_task_id, elapsed_ms: Math.round(performance.now() - startedAt), scan_type: 'deep_scan', scan_result: String(response.data.score), page_type: 'app_scan', entrypoint: 'ai_scan_result'}));
            update({scan: 'ready', scannedOn: getNowScannedString()},
                {...this.checkResult('scan', response.data), analysis_uuid: result.analysis_uuid});
            this.root.announcement.scanLanded();
        } catch (e) {
            const error = reprNetworkError(e);
            // The simple result is still this file's: it stays, and the wall or the banner says why.
            update({scan: before.scan});
            if (this.controller.signal.aborted) return;
            if (!this.root.input.isLimitError(error.code)) {
                this.root.input.setError(error);
            } else {
                this.root.input.showPaywall({trigger: limitErrorToTrigger(error.code), onUnblocked: () => this.rescanDeep(uuid)}, {oneTime: false});
            }
        } finally {
            this.root.history.page = 1;
            this.root.refresh();
        }
    }

    /** One check's payload as this file's result fields — what a live run's poll and a
     *  restored report both put in filesResults. */
    private checkResult(check: RanCheck, data: any): Partial<FileRequestResult> {
        if (check === 'scan') return {...data, ai_score: data.score, segments: data.segmentation_data};
        if (check === 'plagiarism') {
            return {...data, plagiarism_segments: this.root.plagiarism.mapPlagiarismSegments(data.text, data), plagiarism: data};
        }
        // Whole task payload (text + matches + language) — the selectedFile reaction feeds it
        // into grammarScanStore.load. `text` also goes top-level: the file plate widgets read
        // result.text (e.g. the character counter) and grammar may be the only completed check.
        return {text: data.text, grammar: data};
    }

    /** The files this batch ran, each with the analysis that holds its results. */
    ranFiles(): RanFile[] {
        return Array.from(this.filesWaiting.entries()).flatMap(([uuid, state]) => {
            const analysis = this.filesResults.get(uuid)?.analysis_uuid;
            return analysis ? [{uuid, name: state.name, analysis, scannedOn: state.scannedOn, oneTime: state.oneTime}] : [];
        });
    }

    /** A login's ran batch back on the page, each file's results fetched as its public report.
     *  A login without a reload never took the batch away, and a batch or a scan the user
     *  started meanwhile is theirs: all are left alone. */
    async restoreRan({files, modes, selected}: RanBatchDraft) {
        const pageTaken = () => this.isBatchMode || this.root.input.hasAnyText;
        if (pageTaken()) return;
        const reports = await Promise.all(files.map(f => getReport(f.analysis).catch(() => null)));
        if (pageTaken()) return;
        runInAction(() => {
            files.forEach((file, i) => {
                const checks = new Map<string, any>(
                    (reports[i]?.items ?? []).filter(item => item.status === 'success').map(item => [item.type, item.data]));
                if (!checks.size) return;
                const result = {analysis_uuid: file.analysis} as FileRequestResult;
                const state = {} as Record<RanCheck, CheckState>;
                for (const [check, type] of Object.entries(RAN_CHECK_TYPE) as [RanCheck, string][]) {
                    state[check] = checks.has(type) ? 'ready' : 'not_requested';
                    if (checks.has(type)) Object.assign(result, this.checkResult(check, checks.get(type)));
                }
                this.filesWaiting.set(file.uuid, {name: file.name, scannedOn: file.scannedOn, oneTime: file.oneTime, ...state});
                this.filesResults.set(file.uuid, result);
            });
            if (!this.isBatchMode) return;
            this.modes = modes;
            this.openRanSection();
            // No fallback to the first file: FileProcessingBlock does that on desktop, and on a
            // phone a selection opens the results sheet the user did not ask for.
            this.selectFile(selected && this.filesWaiting.has(selected) ? selected : null);
        });
    }

    private openRanSection() {
        const input = this.root.input;
        if (this.sectionRan(input.activeSection)) return;
        const ran = (Object.keys(SECTION_FILE_STATE) as ActiveSection[]).find(section => this.sectionRan(section));
        if (ran) input.setActiveSection(ran);
    }

    /** Queue a partly scanned document again, whole: the next run scans all of it. */
    restagePartial(uuid: string) {
        const partial = this.filesWaiting.get(uuid)?.partial;
        if (partial) this.fileUuidMap.set(uuid, partial.file);
    }

    selectFile(uuid: string | null) {
        this.selectedFile = uuid;
        this.root.input.setIsHighlightSentences(true);
        this.root.input.setIsHighlightWords(true);
    }

    clearStore() {
        // Clear all progress intervals
        this.progressIntervals.forEach(interval => clearInterval(interval));
        this.progressIntervals.clear();
        
        this.fileUuidMap = new Map<string, IFileUUIDValue>();
        this.filesWaiting = new Map<string, IWaitingFile>();
        this.filesResults = new Map<string, FileRequestResult>();
        this.selectedFile = null;
        this.modes = NO_MODES;
        this.controller.abort("Exit batch scan");
        this.controller = new AbortController();
    }

    /** How this file describes itself to the certificate rule — the file plate hands the
     *  same object to its download control, so the icon and the store never disagree. */
    certificateSource(uuid: string): CertificateSource {
        const state = this.filesWaiting.get(uuid);
        const result = this.filesResults.get(uuid);
        return {
            analysisUuid: result?.analysis_uuid,
            aiScore: result?.ai_score,
            plagiarismScore: result?.plagiarism?.score,
            pending: state?.scan === "waiting" || state?.plagiarism === "waiting",
        };
    }

    /** The verdict chips of this file's plate, in tab order: one per tool the batch ran on
     *  it — spinning while the check runs, its verdict once in, "Error" when it failed. */
    resultChips(uuid: string): ResultChip[] {
        const state = this.filesWaiting.get(uuid);
        if (!state) return [];
        const result = this.filesResults.get(uuid);
        // A check that is in without a score failed; it must not wear a clean result's circle.
        const scoreVerdict = (score: number | undefined, kind: ScoreKind): ChipVerdict | null => score == null ? null : {
            value: kind === 'plagiarism' ? scoreToPercent(score, {round: true}) : formatAiScore(score),
            tone: shownTone(score, kind),
        };
        // waitTaskResult resolves a task that ended in `error` too, so a "ready" grammar payload
        // is not guaranteed to carry matches — a crash here would take the whole list down.
        const issues = result?.grammar?.matches ? actionableGrammarMatches(result.grammar).length : 0;
        const chip = (tool: ResultChipTool, check: CheckState, verdict: ChipVerdict | null): ResultChip[] =>
            check === 'not_requested' ? []
                : check !== 'ready' ? [{tool, status: check}]
                    : verdict ? [{tool, status: verdict.tone, value: verdict.value}] : [{tool, status: 'error'}];
        return [
            ...chip('ai', state.scan, scoreVerdict(result?.ai_score, 'ai')),
            ...chip('plagiarism', state.plagiarism, scoreVerdict(result?.plagiarism?.score, 'plagiarism')),
            ...chip('grammar', state.grammar, {value: String(issues), tone: grammarTone(issues)}),
        ];
    }

    canDownloadCertificate(uuid: string) {
        return this.filesWaiting.has(uuid) && hasCertificate(this.certificateSource(uuid));
    }

    /** True when at least one file of the batch has a certificate to download. */
    hasDownloadableCertificates() {
        return Array.from(this.filesWaiting.keys()).some((uuid) => this.canDownloadCertificate(uuid));
    }

    /** Download every available certificate, keeping the batch busy until all settle. */
    async getAllCertificates() {
        if (this.isDownloadingCertificates) return;
        const certificate = this.root.certificate;
        const retry = () => this.getAllCertificates();
        if (!certificate.requireAccess('batch_all', null, retry)) return;

        this.isDownloadingCertificates = true;
        const results = await Promise.allSettled(Array.from(this.filesWaiting.keys()).map(async (uuid) => {
            const analysisUuid = this.filesResults.get(uuid)?.analysis_uuid;
            if (!analysisUuid || !this.canDownloadCertificate(uuid)) return false;
            await downloadCertificate(analysisUuid);
            return true;
        }));
        runInAction(() => { this.isDownloadingCertificates = false; });
        // One event per outcome of the click, the files counted in it — not one per file.
        const saved = results.filter(r => r.status === 'fulfilled' && r.value).length;
        if (saved) void logEvent(gtmCertificate('download', 'batch_all', {files: saved}));
        // One toast for the batch: the files that did download are already saved.
        const failures = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
        if (failures.length) {
            void logEvent(gtmCertificate('download_error', 'batch_all', {error_code: reprNetworkError(failures[0].reason).code, files: failures.length}));
            this.root.toast.error('certificate-download-batch-error', failures[0].reason);
        }
    }

    /** No check is still running. A failed one counts as settled: it will never
     *  finish, and waiting on it used to disable "Get all certificates" forever
     *  for the whole batch — including the files that did produce a certificate. */
    areAllScansComplete() {
        return Array.from(this.filesWaiting.values()).every(file =>
            file.scan !== "waiting" && file.plagiarism !== "waiting"
        );
    }

    isFilesLoading() {
        if (this.fileUuidMap.size === 0) {
            return false;
        }
        // "Loading" means at least one upload is still in flight. A file is
        // considered done the moment its upload settles — success OR failure —
        // so a server-rejected file (which may settle without an id, just a
        // warning/error) never keeps the Run scan button stuck disabled. Once
        // every upload has settled, haveActionableFiles() decides whether
        // there is anything scannable to enable the button for.
        // (The original bug: this checked `characters_count === 0`, but
        // uploading files start with `characters_count: undefined`, so the
        // guard never matched and scanning fired before the BatchFile existed →
        // backend file_with_uuid_not_found.)
        return Array.from(this.fileUuidMap.values()).some((file) => !file.uploaded);
    }

    /** Staged files "Run scan" is about: a file with no intrinsic error is either scannable or
     *  over the live char limit, and both keep the button enabled (the latter opens the paywall
     *  CTA on click). Files with a hard error (empty/language/too short) are never scanned. */
    private actionableFiles(): IFileUUIDValue[] {
        return Array.from(this.fileUuidMap.values()).filter(f => !f.error);
    }

    /** Whether "Run scan" should stay enabled: at least one staged file is actionable. */
    haveActionableFiles() {
        return this.actionableFiles().length > 0;
    }

    /** Live per-file character-limit check against the CURRENT plan limit (read fresh, never
     *  cached at upload). Raising the limit — e.g. after an upgrade — immediately lets a file
     *  that now fits through, instead of a stale upload-time flag keeping it blocked. Only
     *  meaningful for a file that otherwise passes validation (no intrinsic error). */
    private isOverCharLimit(file: IFileUUIDValue, oneTime = false): boolean {
        // A one-time run takes files up to its own cap, whatever the plan's.
        const limit = oneTime ? this.root.pricing?.oneTimeScan?.max_chars : this.root.user.user?.words_per_generation_limit;
        return !!file.characters_count && !!limit && file.characters_count > limit;
    }

    /** Every staged file that passed intrinsic validation, in upload order — the union the
     *  plan-limit partitions below are cut from. */
    private errorFreeFiles(): IFileUUIDValue[] {
        return Array.from(this.fileUuidMap.values()).filter(f => !f.error);
    }

    /** A file's size in the unit the word allowance is measured in. `chars` prorates it to a
     *  prefix run, which sends only the first N characters of the file. */
    private fileWords(file: IFileUUIDValue, chars?: number): number {
        const words = file.words_count ?? file.characters_count ?? 0;
        const total = file.characters_count ?? 0;
        if (chars === undefined || !total || chars >= total) return words;
        return Math.ceil(words * chars / total);
    }

    /** Files that can actually be scanned now: no intrinsic error and within the live
     *  per-file char limit, in upload order. */
    private scannableFiles(): IFileUUIDValue[] {
        return this.errorFreeFiles().filter(f => !this.isOverCharLimit(f));
    }

    countScannableFiles() {
        return this.scannableFiles().length;
    }

    /**
     * Which staged files a one-time run pays for, one scan each: those the plan can't take — past
     * the "Scan first N" prefix, or over its per-file char cap. The prefix runs on the plan in
     * the same run. A batch the plan takes whole has nothing to sell, so a one-time run of it
     * (the toolbar's "Scan once") spends a scan on every file.
     */
    oneTimeUuids(): string[] {
        const errorFree = Array.from(this.fileUuidMap.entries()).filter(([, f]) => !f.error);
        const fitting = new Set(errorFree
            .filter(([, f]) => !this.isOverCharLimit(f))
            .slice(0, this.batchScannableCount())
            .map(([uuid]) => uuid));
        const overflow = errorFree.filter(([uuid]) => !fitting.has(uuid)).map(([uuid]) => uuid);
        return overflow.length ? overflow : errorFree.map(([uuid]) => uuid);
    }

    /** The one file this batch is about, when exactly one staged file is actionable (an
     *  oversized one counts — it is the case in point). Such a batch has no "scan fewer files"
     *  to fall back on, so the paywall offers a prefix of the document instead — see
     *  InputStore.soleDocumentSteps. */
    soleFile(): IFileUUIDValue | undefined {
        const candidates = this.actionableFiles();
        return candidates.length === 1 ? candidates[0] : undefined;
    }

    /** The longest file a one-time run would pay for, in characters — what a one-time scan's
     *  per-file cap is held to. */
    longestOneTimeFileChars(): number {
        const uuids = this.oneTimeUuids();
        return Math.max(0, ...uuids.map(uuid => this.fileUuidMap.get(uuid)?.characters_count ?? 0));
    }

    /** Staged files blocked solely for exceeding the per-file character limit, evaluated live
     *  against the current plan, in upload order. Drive the char-limit paywall on "Run scan"
     *  (their inline banners are suppressed). */
    charLimitFiles(): IFileUUIDValue[] {
        return this.errorFreeFiles().filter(f => this.isOverCharLimit(f));
    }

    /** Error text for a file row (warning triangle + tooltip): the cached intrinsic error if
     *  any, otherwise a live "too long" message with the CURRENT limit — so it clears the
     *  instant an upgrade raises the limit, rather than lingering with the old cap. */
    fileDisplayError(uuid: string): string | undefined {
        const file = this.fileUuidMap.get(uuid);
        if (!file) return undefined;
        if (file.error) return resolveText(file.error);
        if (this.isOverCharLimit(file) && !file.charLimitWaived) {
            return translate('scan-error-file-too-long', {limit: this.root.user.user?.words_per_generation_limit ?? 0});
        }
        return undefined;
    }

    /**
     * What a plan must offer for the WHOLE staged batch to go through, in plan-limit units —
     * the paywall picks the cheapest plan that covers it (see utils/planCoverage).
     *
     * Counts every error-free file, INCLUDING the ones currently blocked by the per-file
     * character cap: a plan that lifts that cap makes them scannable, and their words then
     * count against the allowance too. Sizing the offer off `scannableFiles()` would price a
     * batch smaller than the one the user actually staged. The files the batch already ran
     * count toward `files` as well — they hold their slots against the per-batch cap.
     *
     * `run` prices a narrowed dispatch instead — the free "scan the first N files" / "scan the
     * first part of this document" concessions. The user has already given up the rest, so
     * pricing it in escalates the offer a tier, or past every plan into the no-cover dead end.
     */
    planRequirement(wordCostMultiplier: number, run?: {uuids: string[], prefix?: {uuid: string, text: string}}): PlanRequirement {
        const staged = run
            ? run.uuids.flatMap(uuid => {
                const file = this.fileUuidMap.get(uuid);
                if (!file) return [];
                return [{file, prefixChars: uuid === run.prefix?.uuid ? run.prefix.text.length : undefined}];
            })
            : this.errorFreeFiles().map(file => ({file, prefixChars: undefined as number | undefined}));
        return {
            files: this.planRanCount + staged.length,
            chars: staged.reduce((max, {file, prefixChars}) => Math.max(max, prefixChars ?? file.characters_count ?? 0), 0),
            words: staged.reduce((sum, {file, prefixChars}) => sum + this.fileWords(file, prefixChars), 0) * wordCostMultiplier,
        };
    }

    /** Total words across all scannable files — the real limit a free user hits. */
    get totalScannableWords() {
        return this.scannableFiles().reduce((sum, f) => sum + this.fileWords(f), 0);
    }

    /**
     * How many files the COUNT-based caps still allow, ignoring the word allowance: the open
     * slots of the per-batch file cap, the files the remaining requests pay for, and the per-file count
     * quotas (one deep scan, grammar check and plagiarism check per file). Nothing a shorter
     * text buys back — so a run this returns 0 for has no free option, however far it is trimmed.
     *
     * `modes` prices an offer the user hasn't taken yet, exactly as batchScannableCount does.
     */
    scannableFileCap(modes: ScanModes = {}): number {
        return Math.min(
            this.openDocumentSlots,
            this.root.input.remainingRuns(modes) ?? Number.POSITIVE_INFINITY,
            // Each file spends one deep scan / grammar check / plagiarism check, so any of those
            // quotas caps how many files can run — otherwise the run starts and the files past it
            // come back 403.
            this.root.input.remainingDeepScans(modes) ?? Number.POSITIVE_INFINITY,
            this.root.input.remainingGrammarChecks(modes) ?? Number.POSITIVE_INFINITY,
            this.root.input.remainingPlagiarismChecks(modes) ?? Number.POSITIVE_INFINITY,
        );
    }

    /**
     * Size of the largest prefix of scannable files (upload order) that fits every active
     * limit: everything scannableFileCap covers, plus the remaining word allowance. Drives the
     * "Scan first N files" CTA.
     *
     * `modes` is the run the offer being priced would apply — basicScanStep's for "continue
     * with basic scan". The downgrade both frees words and stops spending the add-on's quota,
     * so the count has to come from one source: whatever the run will actually be charged for.
     */
    batchScannableCount(modes: ScanModes = {}): number {
        const fileCap = this.scannableFileCap(modes);
        const wordsLimit = this.root.input.affordableWords(modes);
        let count = 0;
        let words = 0;
        for (const f of this.scannableFiles()) {
            if (count >= fileCap) break;
            const w = this.fileWords(f);
            if (words + w > wordsLimit) break;
            words += w;
            count += 1;
        }
        return count;
    }

    onFileDrop(acceptedFiles: File[]) {
        // The scan page stays interactive while the app is still loading (see
        // MainPage BlurredScreen), so a document can be chosen before the session is
        // ready. At that point `user` is null (can't run the plan-limit gate) and there
        // is no token (uploadFile would send `Authorization: Token null`). Mirror the
        // text input's "capture now, act once ready" behaviour: stash the selection and
        // replay this exact call the moment the session becomes ready.
        if (!this.root.user.isReady()) {
            const firstPending = this._pendingDrop === null;
            this._pendingDrop = acceptedFiles;
            if (firstPending) {
                when(
                    () => this.root.user.isReady(),
                    () => {
                        const files = this._pendingDrop;
                        this._pendingDrop = null;
                        if (files && files.length) this.onFileDrop(files);
                    },
                );
            }
            return;
        }

        const input = this.root.input;
        const user = this.root.user.user;

        // Files replace a typed text, any tab's. A batch that ran fills the editor too — with the selected
        // file — and that one stays: the drop adds to the batch (see the constructor's reaction).
        if (input.hasAnyText && !this.isBatchMode) {
            input.clear();
        }

        if (!user?.can_upload_file) {
            if (this.root.user.isAnonymous) { this.promptLoginAndResumeDrop(acceptedFiles); }
            else if (this.root.user.isFree()) {
                // No requirement: this is a boolean wall ("uploading is off for this account"),
                // not a sized one. The dropped files are rejected, never staged, so sizing the
                // offer by how many were dropped would sell Enterprise for a run that doesn't
                // exist — and the count isn't what's blocking anyway.
                this.root.modal.showModal("increase-limits-form", {trigger: 'upload_documents'});
            }
            else { input.setError(() => translate('scan-error-doc-limit', {limit: user?.batch_doc_upload ?? 0}), "rich_limit"); }
            return;
        }

        if (acceptedFiles.length === 0) {
            input.setError(() => translate('scan-error-unexpected-extension'));
            return;
        }

        // The per-batch file-count limit is intentionally NOT enforced here — the user
        // may stage more files than their plan allows; the counter turns red and the
        // limit is applied on "Run scan" (see inputStore.makeRunScanRequest).

        this.enterFromTab();
        for (let i = 0; i < acceptedFiles.length; i++) {
            const file = acceptedFiles[i];
            if (file.size > 1024 * 1024 * (user?.document_upload_limit_mb ?? 0)) {
                // The cap is global across registered plans, so there is no
                // upgrade path to surface — anons get the auth prompt, everyone
                // else just sees the size error.
                // Only the files this drop hasn't staged yet are resumed: the ones before
                // this one are already in the list, and re-dropping them would duplicate them.
                if (this.root.user.isAnonymous) { this.promptLoginAndResumeDrop(acceptedFiles.slice(i)); }
                else { input.setError(() => translate('scan-error-file-size', {limit: user?.document_upload_limit_mb ?? 0})); }
                return;
            }

            this.addFile(file);
        }

        input.setError();
    }

    /**
     * The anon's upload walls are the only ones a login actually lifts (5 MB against every
     * registered plan's 100 MB), so the drop is replayed the moment the account exists instead
     * of leaving the user to find the file again — and the replay re-runs every gate, so one
     * that is still blocked opens its own paywall. The replay lives in memory, which is as far
     * as a File can travel: an OAuth login reloads the page and takes the drop with it.
     */
    private promptLoginAndResumeDrop(files: File[]) {
        this.root.input.promptLogin('document', {resume: () => this.onFileDrop(files)});
    }
}
